'use client';

import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Else, If, Then, When } from 'react-if';

import cn from '@/lib/clsxm';
import handleGA4Event, { eventTracker } from '@/lib/service-workers';
import { useClientSmallScreen } from '@/lib/user-agent/client';

import Breadcrumb from '@/components/@base/breadcrumb';
import { BreadcrumbList } from '@/components/@base/breadcrumb/type';
import Container from '@/components/@base/container';
import { CONTAINER_TAG_TYPE } from '@/components/@base/container/type';
import { usePostAddToCart } from '@/components/app/cart/services/hooks';
import Section from '@/components/app/product/components/@shared/section';
import ActionButtons from '@/components/app/product/components/action-buttons';
import AffiliateCard from '@/components/app/product/components/affiliate-card';
import { PageBg } from '@/components/app/product/components/background';
import Badges from '@/components/app/product/components/badge-slider';
import Banner from '@/components/app/product/components/banner';
import DiscountCountdown from '@/components/app/product/components/discount-countdown';
import Highlights from '@/components/app/product/components/highlights';
import PriceInfo from '@/components/app/product/components/price-info';
import RecommendedProducts from '@/components/app/product/components/recommended-products';
import ReleaseDetails from '@/components/app/product/components/release-details';
import RateAction from '@/components/app/product/components/side-bar/rate-action';
import StoreCard from '@/components/app/product/components/store-card';
import SuggestedAlternativeProducts from '@/components/app/product/components/suggested-alternative-products';
import Tabs from '@/components/app/product/components/tabs';
import Tags from '@/components/app/product/components/tags';
import Title from '@/components/app/product/components/title';
import Props from '@/components/app/product/components/type';
import { isBundle } from '@/components/app/product/helpers';
import { getSingleProductOptions } from '@/components/app/product/services/hooks';
import { useGetProfile } from '@/components/layout/services/hooks';

import {
  DATA_CY_AFFILIATE_CARD_PRODUCTS_MOBILE,
  DATA_CY_HIGHLIGHTS_PRODUCTS_MOBILE,
  DATA_CY_PRODUCT_CART,
  DATA_CY_PRODUCT_CART_MOBILE,
  DATA_CY_PRODUCT_DEMO_CONTAINER,
  DATA_CY_PRODUCT_NAME,
  DATA_CY_PRODUCT_TAGS,
  DATA_CY_VENDOR_PRODUCTS,
} from '@/constant/cypress';
import localization, { pageLevelLocalization } from '@/constant/localization';
import { GET_CART } from '@/constant/query-key';
import ProductModel from '@/models/product.model';

const { product: productLocalization } = pageLevelLocalization;

export default function Product({ slug }: Props) {
  const { data } = useSuspenseQuery(getSingleProductOptions(slug));
  const isSmallScreen = useClientSmallScreen();
  const { profile } = useGetProfile();
  const [isClient, setIsClient] = useState(false);
  const isReadySite = data?.isReadySite;
  const hasTrackedViewRef = useRef(false);
  useEffect(() => {
    setIsClient(true);
  }, []);
  const productData = {
    ...data,
    highlights:
      data.highlights?.length > 0
        ? data.highlights
        : [
            productLocalization.defaultHighlight4,
            productLocalization.defaultHighlight1,
            productLocalization.defaultHighlight3,
            productLocalization.defaultHighlight5,
            productLocalization.defaultHighlight6,
          ],
    saleEndedAt: data.saleEndedAt ? Number(data.saleEndedAt) * 1000 : 0,
    saleStartedAt: data.saleStartedAt ? Number(data.saleStartedAt) * 1000 : 0,
    isOnSale:
      data.isOnSale && (isClient ? data.saleEndedAt * 1000 > Date.now() : data.isOnSale),
  };
  const hasDiscount =
    (productData.isOnSale && productData.discount > 0) ||
    (productData.market === 'Bundle' && productData.discount > 0);

  let customBreadcrumbList: BreadcrumbList[] = [
    { label: localization.home, url: '/', active: false },
  ];

  if (productData.categories && productData.categories.length > 0) {
    customBreadcrumbList = productData.categories
      .filter((category) => category.slug !== 'made-in-iran')
      .map((category) => ({
        label: category.title,
        url: `/web/category/${category.slug}`,
        active: false,
      }));
  }

  useEffect(() => {
    if (typeof window !== 'undefined' && !hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true;
      eventTracker(profile?.mobile, 'product_view', new Date().toISOString(), {
        url: window?.location?.href,
        category: productData?.categories?.map((item) => ({
          $oid: item?.id,
        })),
        vendor: productData?.store?.id,
        price: productData?.price,
      });
      let viewItemData = {
        item_id: productData?.id,
        item_name: productData?.title,
        price: productData?.price,
        affiliation: productData?.store?.slug,
        index: 0,
        quantity: 1,
        discount: productData?.discount,
      };

      productData?.categories?.map((item, index) => {
        viewItemData[`item_category${index !== 0 ? index + 1 : ''}`] = item.title;
      });
      handleGA4Event('view_item', [viewItemData], {
        value: productData?.price,
        currency: 'USD',
      });
    }
  }, [productData]);
  return (
    <Container className='bg-[#F9FAFC]' type={CONTAINER_TAG_TYPE.MAIN}>
      <Suspense>
        <AddToCartParam data={productData} />
      </Suspense>
      <Container
        id='productSpecifications'
        className='relative z-10 overflow-hidden bg-[#FFFFFF] md:pb-7'
      >
        <PageBg />
        <Container className='max-w-[1279px] md:mx-auto md:flex md:items-end md:gap-[30px]'>
          <Container className='md:flex-1'>
            <Breadcrumb
              className='mx-auto px-4 py-5 text-[#737373] md:px-0'
              excludeRoutes={['web']}
              customBreadCrumbList={customBreadcrumbList}
            />
            <Container
              data-cy={DATA_CY_PRODUCT_CART}
              className='md:grid md:max-h-[400px] md:min-h-[360px] md:flex-1 md:grid-cols-3 md:items-stretch md:rounded-md md:bg-[#FFFFFF] md:p-0 md:shadow-[0px_10px_25px_0px_#CECECE40]'
            >
              <Container className='productInfo border-l-[1px] border-l-[#F6F6F6] md:col-span-2 md:flex md:flex-col'>
                <Title
                  title={productData.title}
                  store={productData.store}
                  medals={productData.medals}
                  data-cy={DATA_CY_PRODUCT_NAME}
                />
                {isSmallScreen && (
                  <Container>
                    {productData.isOnSale && (
                      <DiscountCountdown
                        saleStartedAt={productData.saleStartedAt}
                        saleEndedAt={productData.saleEndedAt}
                        remainingAmount={productData.saleRemainingAmount}
                        totalAmount={productData.saleTotalAmount}
                      />
                    )}
                    <Banner
                      hasDiscount={hasDiscount}
                      cover={productData.thumbnail}
                      alternativeCover={productData.alternateCover}
                      sellable={productData.sellable}
                      galleries={productData.galleries}
                    />
                  </Container>
                )}
                <PriceInfo slug={slug} />
                {productData.sellable && !isSmallScreen && (
                  <ActionButtons
                    isFree={productData.price === 0 && !productData.hasAddonFreeProduct}
                    slug={slug}
                    productId={productData.id}
                    // className='hidden md:block'
                    hasPreviewButton={!isBundle(productData)}
                    isSubscription={productData?.isSubscription}
                    subscriptionDefaultPriceIsOneYear={
                      productData?.subscriptionDefaultPriceIsOneYear
                    }
                  />
                )}
              </Container>
              <Container className='hidden p-4 pb-[9px] md:block'>
                <Container className='rounded-md bg-[linear-gradient(180deg,_#F9FAFC_0%,_#FFFFFF_30.97%)] px-[25px] pt-[30px] pb-[18px] md:col-span-1 md:flex md:flex-col md:justify-around'>
                  <If
                    condition={
                      productData.sellable ||
                      (!productData.sellable &&
                        productData.alternativeSuggestions.length === 0)
                    }
                  >
                    <Then>
                      <Highlights
                        highlights={productData.highlights}
                        className={cn(
                          !productData.bestSellerBadge &&
                            !productData.digiPayBadge &&
                            'border-none!',
                        )}
                        promoteVideo={productData?.promoteVideo}
                        promoteVideoCover={productData?.promoteVideoCover}
                      />
                    </Then>
                    <Else>
                      <SuggestedAlternativeProducts
                        data={productData.alternativeSuggestions}
                      />
                    </Else>
                  </If>
                </Container>
                <Badges productData={productData} />
              </Container>
            </Container>
          </Container>
          <Container
            data-cy={DATA_CY_PRODUCT_DEMO_CONTAINER}
            className='hidden md:block md:max-w-[360px]'
          >
            {productData.isOnSale && (
              <DiscountCountdown
                saleStartedAt={productData.saleStartedAt}
                saleEndedAt={productData.saleEndedAt}
                remainingAmount={productData.saleRemainingAmount}
                totalAmount={productData.saleTotalAmount}
              />
            )}
            <Banner
              hasDiscount={hasDiscount}
              cover={productData.thumbnail}
              alternativeCover={productData.alternateCover}
              sellable={productData.sellable}
              galleries={productData.galleries}
            />
          </Container>
        </Container>
      </Container>
      {productData.sellable && isSmallScreen && (
        <ActionButtons
          dataCy={DATA_CY_PRODUCT_CART_MOBILE}
          isFree={productData.price === 0}
          slug={slug}
          productId={productData.id}
          // className='block md:hidden'
          hasPreviewButton={!isBundle(productData)}
          isSubscription={productData?.isSubscription}
          subscriptionDefaultPriceIsOneYear={productData?.subscriptionDefaultPriceIsOneYear}
        />
      )}
      <Tabs
        slug={slug}
        id={productData.id}
        rate={productData.rate}
        market={productData.market}
        productId={productData.id}
        isSubscription={productData?.isSubscription}
        store={productData?.store?.slug}
        tableOfContents={productData?.tableOfContents}
        reviews={productData?.reviews}
        hasQuestions={!!productData?.questions?.length}
        isReadySite={isReadySite}
        aiReviewSummary={productData?.aiReviewSummary}
      />
      <Section data-cy={DATA_CY_HIGHLIGHTS_PRODUCTS_MOBILE} className='md:hidden'>
        <Highlights
          promoteVideo={productData?.promoteVideo}
          promoteVideoCover={productData?.promoteVideoCover}
          highlights={productData.highlights}
          className='md:hidden'
        />

        <ReleaseDetails
          releaseDate={productData.releaseDate}
          version={productData.version}
          updateDate={productData.updateDate}
          rate={productData.rate}
        />
        <RateAction productId={productData.id} slug={slug} className='mt-6' />
      </Section>
      <When condition={productData?.store?.id}>
        <StoreCard {...productData.store} className='md:hidden' />
      </When>

      {productData.sellable && productData.price > 0 && (
        <AffiliateCard
          data-cy={DATA_CY_AFFILIATE_CARD_PRODUCTS_MOBILE}
          slug={slug}
          price={productData.affiliatePrice}
          termsUrl={productData.affiliateTermsUrl}
          productName={productData.title}
          className='md:hidden'
        />
      )}
      <Section
        data-cy={DATA_CY_VENDOR_PRODUCTS}
        expandable={isSmallScreen}
        className='max-w-[1279px] px-4 md:mx-auto'
      >
        {productData.vendorProducts.length > 0 && (
          <RecommendedProducts
            products={productData?.vendorProducts}
            logo={productData?.store?.logo}
            title={`${productLocalization.otherVendorProducts} ${productData?.store?.name}`}
          />
        )}
        {!isSmallScreen && productData.relatedProducts.length > 0 && (
          <>
            <Container className='mt-3.5 mb-6 h-[1px] w-full bg-[#F4F5F6]' />
            <RecommendedProducts
              products={productData.relatedProducts}
              logo={productData.icon}
              title={`${productLocalization.relatedProducts} ${productData.title}`}
            />
          </>
        )}
      </Section>
      {productData.tags.length > 0 && (
        <Tags
          data-cy={DATA_CY_PRODUCT_TAGS}
          tags={productData.tags}
          className='max-w-[1279px] px-4 md:mx-auto'
        />
      )}
    </Container>
  );
}

function AddToCartParam({ data }: { data: ProductModel }) {
  const searchParams = useSearchParams();
  const { mutate: addToCartMutate } = usePostAddToCart();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has('add-to-cart') && data) {
      addToCartMutate(
        { id: data.id },
        {
          onSuccess: (res) => {
            queryClient.setQueryData([GET_CART], res);
          },
        },
      );
    }
  }, [data]);

  return null;
}
